import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '@clerk/clerk-react';

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${props => props.theme.zIndex.fixed};
  background: ${props => props.scrolled 
    ? 'rgba(255, 255, 255, 0.95)' 
    : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(20px)' : 'none'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: ${props => props.scrolled 
    ? '1px solid rgba(46, 204, 113, 0.2)' 
    : 'none'};
  box-shadow: ${props => props.scrolled 
    ? '0 8px 32px rgba(0, 0, 0, 0.1)' 
    : 'none'};
  width: 100%;
  box-sizing: border-box;
  overflow: visible; /* Stelle sicher, dass alle Elemente sichtbar sind */
`;

const NavContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: clamp(1rem, 2vw, 2rem);
  min-width: 0; /* Ermöglicht Shrinking */
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1100px) {
    gap: clamp(0.75rem, 1.5vw, 1.5rem);
    padding: 1rem 1.5rem;
  }
  
  @media (max-width: 1000px) {
    gap: clamp(0.5rem, 1vw, 1rem);
    padding: 1rem 1.25rem;
  }
  
  @media (max-width: 900px) {
    gap: 0.75rem;
    padding: 1rem 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const Logo = styled(Link)`
  text-decoration: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  padding: 0.5rem;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0; /* Logo darf nicht schrumpfen */
  min-width: fit-content; /* Stelle sicher, dass Logo vollständig sichtbar ist */
  overflow: visible; /* Stelle sicher, dass Text nicht abgeschnitten wird */
  
  &:hover {
    transform: translateY(-2px);
    background: ${props => props.scrolled 
      ? 'rgba(46, 204, 113, 0.1)' 
      : 'rgba(255, 255, 255, 0.1)'};
  }
  
  @media (max-width: 1000px) {
    padding: 0.4rem;
    gap: 0.6rem;
  }
  
  @media (max-width: 900px) {
    padding: 0.35rem;
    gap: 0.5rem;
  }
`;

const LogoImage = styled.img`
  height: clamp(2rem, 3vw, 2.5rem);
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
  filter: ${props => props.scrolled ? 'none' : 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    height: 2rem;
  }
`;

const LogoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const LogoText = styled.h2`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(1.4rem, 2.5vw, 2rem);
  font-weight: 700;
  color: ${props => props.scrolled ? props.theme.colors.primary : 'white'};
  margin: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-shadow: ${props => props.scrolled ? 'none' : '2px 2px 4px rgba(0,0,0,0.3)'};
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
  
  @media (max-width: 1000px) {
    font-size: clamp(1.3rem, 2vw, 1.8rem);
  }
  
  @media (max-width: 900px) {
    font-size: clamp(1.2rem, 1.8vw, 1.6rem);
  }
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const LogoSubtitle = styled.span`
  font-size: 0.85rem;
  color: ${props => props.scrolled ? props.theme.colors.textLight : 'rgba(255, 255, 255, 0.9)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: -0.25rem;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: clamp(0.35rem, 0.5vw, 0.5rem);
  gap: clamp(0.3rem, 0.4vw, 0.5rem);
  background: ${props => props.scrolled 
    ? 'rgba(46, 204, 113, 0.08)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 25px;
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.scrolled 
    ? 'rgba(46, 204, 113, 0.2)' 
    : 'rgba(255, 255, 255, 0.2)'};
  width: fit-content; /* Container nur so breit wie Inhalt - kein überschüssiger Platz */
  flex-shrink: 0; /* Verhindert Schrumpfen */
  overflow-x: visible; /* Alle Elemente sichtbar machen */
  overflow-y: visible;
  justify-content: center; /* Items zentriert im Container */
  flex-wrap: nowrap; /* Verhindert Umbruch */
  align-items: center;
  
  @media (max-width: 1100px) {
    padding: clamp(0.3rem, 0.4vw, 0.45rem);
    gap: clamp(0.25rem, 0.35vw, 0.4rem);
  }
  
  @media (max-width: 1000px) {
    padding: clamp(0.25rem, 0.3vw, 0.4rem);
    gap: clamp(0.2rem, 0.3vw, 0.35rem);
  }
  
  @media (max-width: 900px) {
    padding: 0.3rem;
    gap: 0.25rem;
  }
  
  /* Mobile Menu Styles - nur bei 768px und kleiner */
  @media (max-width: 768px) {
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    width: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    padding: 1.5rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    transform: translateY(${props => props.isOpen ? '0' : '-200%'});
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    gap: 0.5rem;
    border: none;
    border-bottom: 3px solid ${props => props.theme.colors.primary};
    z-index: ${props => props.theme.zIndex.modal};
    overflow-x: visible;
    overflow-y: auto;
    max-height: calc(100vh - 80px);
  }
  
  /* Stelle sicher, dass Desktop-Menü zwischen 769px und 900px sichtbar bleibt */
  @media (min-width: 769px) and (max-width: 900px) {
    display: flex !important;
    padding: clamp(0.2rem, 0.25vw, 0.3rem);
    gap: clamp(0.15rem, 0.2vw, 0.25rem);
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  font-weight: 500;
  font-size: clamp(0.8rem, 1vw, 0.95rem);
  color: ${props => {
    if (props.$isMobile) return props.theme.colors.text;
    return props.scrolled ? props.theme.colors.text : 'white';
  }};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  padding: clamp(0.6rem, 0.8vw, 0.75rem) clamp(1rem, 1.5vw, 1.5rem);
  border-radius: 20px;
  display: flex;
  align-items: center;
  letter-spacing: 0.3px;
  white-space: nowrap; /* Verhindert Textumbruch */
  flex-shrink: 0; /* Links schrumpfen nicht */
  min-width: fit-content;
  overflow: visible; /* Stelle sicher, dass Text vollständig sichtbar ist */
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background: ${props => {
      if (props.$isMobile) return 'rgba(46, 204, 113, 0.1)';
      return props.scrolled 
        ? 'rgba(46, 204, 113, 0.15)' 
        : 'rgba(255, 255, 255, 0.2)';
    }};
    transform: translateY(-1px);
  }
  
  &.active {
    color: white;
    background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    box-shadow: 0 4px 15px rgba(46, 204, 113, 0.4);
    transform: translateY(-2px);
  }
  
  @media (max-width: 1100px) {
    font-size: clamp(0.75rem, 0.9vw, 0.9rem);
    padding: clamp(0.55rem, 0.7vw, 0.7rem) clamp(0.9rem, 1.3vw, 1.3rem);
  }
  
  @media (max-width: 1000px) {
    font-size: clamp(0.7rem, 0.85vw, 0.85rem);
    padding: clamp(0.5rem, 0.65vw, 0.65rem) clamp(0.8rem, 1.2vw, 1.2rem);
  }
  
  @media (max-width: 900px) {
    font-size: clamp(0.65rem, 0.8vw, 0.8rem);
    padding: clamp(0.45rem, 0.6vw, 0.6rem) clamp(0.7rem, 1vw, 1rem);
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    border-radius: 15px;
    width: 100%;
    background: ${props => props.className?.includes('active') 
      ? 'linear-gradient(45deg, #2ecc71, #27ae60)' 
      : 'rgba(255, 255, 255, 0.7)'};
    color: ${props => props.className?.includes('active') ? 'white' : props.theme.colors.text};
    border: 1px solid ${props => props.className?.includes('active') 
      ? 'transparent' 
      : 'rgba(46, 204, 113, 0.2)'};
    white-space: normal;
    flex-shrink: 1;
    overflow: visible;
    
    &:hover {
      background: ${props => props.className?.includes('active') 
        ? 'linear-gradient(45deg, #2ecc71, #27ae60)' 
        : 'rgba(46, 204, 113, 0.15)'};
      transform: translateY(-2px);
    }
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: ${props => props.scrolled 
    ? 'rgba(46, 204, 113, 0.1)' 
    : 'rgba(255, 255, 255, 0.15)'};
  border: 2px solid ${props => props.scrolled 
    ? 'rgba(46, 204, 113, 0.3)' 
    : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 12px;
  cursor: pointer;
  padding: 0.75rem;
  z-index: ${props => props.theme.zIndex.modal};
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  flex-shrink: 0; /* MenuToggle darf nicht schrumpfen */
  
  &:hover {
    background: ${props => props.scrolled 
      ? 'rgba(46, 204, 113, 0.2)' 
      : 'rgba(255, 255, 255, 0.25)'};
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    display: block;
  }
  
  @media (min-width: 769px) {
    display: none; /* Verstecke MenuToggle auf Desktop */
  }
`;

const MenuBar = styled.span`
  display: block;
  width: 22px;
  height: 2.5px;
  background: ${props => props.scrolled ? props.theme.colors.primary : 'white'};
  margin: 4px 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
  border-radius: 2px;
  
  &:nth-child(1) {
    transform: ${props => props.isOpen 
      ? 'rotate(45deg) translate(5px, 5px)' 
      : 'none'};
  }
  
  &:nth-child(2) {
    opacity: ${props => props.isOpen ? '0' : '1'};
    transform: ${props => props.isOpen ? 'scale(0)' : 'scale(1)'};
  }
  
  &:nth-child(3) {
    transform: ${props => props.isOpen 
      ? 'rotate(-45deg) translate(5px, -5px)' 
      : 'none'};
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${props => props.theme.zIndex.modal - 1};
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isSignedIn } = useUser();
  
  // Bestimme ob wir auf der Startseite sind
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auf anderen Seiten als der Startseite soll die Navbar immer einen Hintergrund haben
  const shouldShowBackground = !isHomePage || scrolled;

  useEffect(() => {
    // Schließe Mobile Menu bei Route-Wechsel
    setMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { path: '/', label: 'Startseite' },
    { path: '/marcella', label: 'Marcella' },
    { path: '/kurse', label: 'Kurse' },
    { path: '/team', label: 'Team' },
    { path: '/ponys', label: 'Ponys' },
    { path: '/institutionen', label: 'Institutionen' },
    { path: '/wochenplan', label: 'Wochenplan' }
  ];

  // Admin-Link hinzufügen wenn User eingeloggt und Admin ist
  const isAdmin = isSignedIn && user?.publicMetadata?.role === 'admin';
  if (isAdmin) {
    navItems.push({ path: '/admin', label: 'Admin' });
  }

  // Icons für jede Seite
  const getIconForPage = (path) => {
    const icons = {
      '/': '🏠',
      '/marcella': '👩‍🏫',
      '/kurse': '🎠',
      '/team': '👥',
      '/ponys': '🐴',
      '/institutionen': '🏛️',
      '/wochenplan': '📅',
      '/admin': '⚙️'
    };
    return <span style={{marginRight: '0.5rem'}}>{icons[path] || '•'}</span>;
  };

  return (
    <NavbarContainer scrolled={shouldShowBackground}>
      <NavContent>
        <Logo to="/">
          <LogoImage src="/assets/logo.webp" alt="Der Ponystall Logo" scrolled={shouldShowBackground} />
          <LogoTextContainer>
            <LogoText scrolled={shouldShowBackground}>DerPonystall</LogoText>
          </LogoTextContainer>
        </Logo>

        <NavMenu isOpen={mobileMenuOpen} scrolled={shouldShowBackground}>
          {navItems.map((item, index) => (
            <motion.li 
              key={item.path}
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.4, 
                delay: mobileMenuOpen ? index * 0.08 : 0,
                ease: "easeOut"
              }}
            >
              <NavLink 
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
                scrolled={shouldShowBackground}
                $isMobile={window.innerWidth <= 768}
                onClick={() => setMobileMenuOpen(false)}
              >
                {getIconForPage(item.path)} {item.label}
              </NavLink>
            </motion.li>
          ))}
        </NavMenu>

        <MenuToggle 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Navigation öffnen/schließen"
          scrolled={shouldShowBackground}
        >
          <MenuBar scrolled={shouldShowBackground} isOpen={mobileMenuOpen} />
          <MenuBar scrolled={shouldShowBackground} isOpen={mobileMenuOpen} />
          <MenuBar scrolled={shouldShowBackground} isOpen={mobileMenuOpen} />
        </MenuToggle>
      </NavContent>

      <AnimatePresence>
        {mobileMenuOpen && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </NavbarContainer>
  );
};

export default Navbar;